import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Badge } from 'primereact/badge';
import { Avatar } from 'primereact/avatar';
import { Dropdown } from 'primereact/dropdown';
import { Timeline } from 'primereact/timeline';
import { Message } from 'primereact/message';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { useAuth } from '../context/AuthContext';
import { ticketService } from '../services/ticketService';
import { timeTrackingService } from '../services/timeTrackingService';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../utils/permissions';
import { io, Socket } from 'socket.io-client';
import { Ticket, StatusHistory } from '../types';
import { getAssetUrl } from '../utils/assets';
import { readChatImageAsDataUrl } from '../utils/imageFile';
import { useDateTime } from '../hooks/useDateTime';
import './TicketDetails.css';

interface ChatMessage {
  _id: string;
  sender: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  message: string;
  attachments?: string[];
  createdAt: string;
}

const TicketDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { can } = usePermissions();
  const { formatDateTime, formatTime: formatClockTime } = useDateTime();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<string[]>([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  // Ukupno vreme svih agenata na tiketu (za END_USER, CLIENT_ADMIN i SUPER_ADMIN)
  const [totalTime, setTotalTime] = useState<{ 
    actualMinutes: number;
    actualHours: number;
    actualMinutesRemainder: number;
    billableMinutes: number; 
    billableHours: number;
    billableMinutesRemainder: number;
  } | null>(null);
  // Vreme pojedinačnog agenta na tiketu (samo za AGENT)
  const [agentTime, setAgentTime] = useState<{
    hasWorked: boolean;
    actualMinutes: number;
    actualHours: number;
    actualMinutesRemainder: number;
    billableMinutes: number;
    billableHours: number;
    billableMinutesRemainder: number;
  } | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const socketRef = useRef<Socket | null>(null);

  // Format time function - prikazuje sate i minute u formatu "1h 30min", "2h 15min"
  const formatTime = (hours: number, minutesRemainder: number): string => {
    if (hours === 0) {
      return `${minutesRemainder}min`;
    } else if (minutesRemainder === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutesRemainder}min`;
    }
  };

  // Status options
  const statusOptions = [
    { label: 'New', value: 'NEW' },
    { label: 'Open', value: 'OPEN' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Closed', value: 'CLOSED' },
    { label: 'Pending Approval', value: 'PENDING_APPROVAL' },
    { label: 'Rejected', value: 'REJECTED' }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTicket();
    loadMessages();
    loadActivities();
    
    // Setup Socket.IO
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
    const socket = io(socketUrl);
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected for ticket chat');
      socket.emit('join-ticket', id);
    });

    socket.on('new-message', (msg: any) => {
      console.log('Received new message:', msg);
      // Samo dodaj poruku ako je validna i ima sender podatke
      if (msg && msg.sender && msg.sender.firstName) {
        setMessages(prev => [...prev, msg]);
      } else {
        // Ako nema sender podatke, reload sve poruke
        loadMessages();
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTicket = async () => {
    try {
      setLoading(true);
      const data = await ticketService.getTicket(id!);
      console.log('📊 Loaded ticket data:', data);
      console.log('📜 Status History:', data.statusHistory);
      setTicket(data);
      
      // Učitaj vreme u zavisnosti od uloge korisnika
      if (data.status === 'CLOSED' || data.status === 'RESOLVED') {
        if (user?.role === 'AGENT') {
          // AGENT vidi samo svoj actual time
          loadAgentTime();
        } else if (user?.role === 'END_USER' || user?.role === 'CLIENT_ADMIN' || user?.role === 'SUPER_ADMIN') {
          // END_USER, CLIENT_ADMIN i SUPER_ADMIN vide ukupno vreme svih agenata
          loadTotalTime();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading ticket');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const data = await ticketService.getTicketMessages(id!);
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
    }
  };

  const loadActivities = async () => {
    try {
      const data = await ticketService.getTicketActivity(id!);
      console.log('📋 Loaded activities:', data);
      setActivities(data);
    } catch (err) {
      console.error('Error loading activities:', err);
    }
  };

  const loadTotalTime = async () => {
    try {
      const data = await timeTrackingService.getTicketTotalTime(id!);
      setTotalTime({
        actualMinutes: data.actualMinutes,
        actualHours: data.actualHours,
        actualMinutesRemainder: data.actualMinutesRemainder,
        billableMinutes: data.billableMinutes,
        billableHours: data.billableHours,
        billableMinutesRemainder: data.billableMinutesRemainder
      });
    } catch (err) {
      console.error('Error loading total time:', err);
    }
  };

  // Load agent time on ticket (samo za AGENT - vidi samo svoj actual time)
  const loadAgentTime = async () => {
    if (!user || user.role !== 'AGENT') return;
    
    try {
      // Koristi novu rutu za agente
      const data = await timeTrackingService.getMyTimeOnTicket(id!);
      setAgentTime({
        hasWorked: data.hasWorked,
        actualMinutes: data.actualMinutes,
        actualHours: data.actualHours,
        actualMinutesRemainder: data.actualMinutesRemainder,
        billableMinutes: 0, // Agent ne vidi billable vreme
        billableHours: 0,
        billableMinutesRemainder: 0
      });
    } catch (err) {
      console.error('Error loading agent time:', err);
      setAgentTime({ hasWorked: false, actualMinutes: 0, actualHours: 0, actualMinutesRemainder: 0, billableMinutes: 0, billableHours: 0, billableMinutesRemainder: 0 });
    }
  };

  const addChatImage = async (file: File) => {
    setAttachmentError('');
    try {
      const dataUrl = await readChatImageAsDataUrl(file);
      setPendingAttachments((current) => [...current, dataUrl]);
    } catch (error: any) {
      setAttachmentError(error.message || 'Could not attach image');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && pendingAttachments.length === 0) return;

    try {
      setSending(true);
      const message = await ticketService.sendMessage(id!, newMessage, pendingAttachments);
      
      socketRef.current?.emit('send-message', {
        ticketId: id,
        message,
        sender: user
      });

      setNewMessage('');
      setPendingAttachments([]);
      setAttachmentError('');
      loadActivities();
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await ticketService.updateTicket(id!, { status: newStatus as any });
      setTicket(prev => prev ? { ...prev, status: newStatus as any } : null);
      loadActivities(); // Reload activities
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const getStatusSeverity = (status: string) => {
    const severityMap: any = {
      NEW: 'info',
      OPEN: 'success',
      IN_PROGRESS: 'warning',
      RESOLVED: 'success',
      CLOSED: 'secondary',
      PENDING_APPROVAL: 'warning',
      REJECTED: 'danger'
    };
    return severityMap[status] || 'info';
  };

  const getStatusLabel = (status: string) => {
    const labelMap: any = {
      NEW: 'New',
      OPEN: 'Open',
      IN_PROGRESS: 'In Progress',
      RESOLVED: 'Resolved',
      CLOSED: 'Closed',
      PENDING_APPROVAL: 'Pending Approval',
      REJECTED: 'Rejected'
    };
    return labelMap[status] || status;
  };

  const handleApproveTicket = async () => {
    try {
      await ticketService.approveTicket(id!);
      loadTicket();
      loadActivities();
    } catch (err) {
      console.error('Error approving ticket:', err);
    }
  };

  const handleRejectTicket = async () => {
    if (!rejectionReason.trim()) {
      alert('Please enter a reason for rejection');
      return;
    }
    
    try {
      await ticketService.rejectTicket(id!, rejectionReason);
      setRejectDialogVisible(false);
      setRejectionReason('');
      loadTicket();
      loadActivities();
    } catch (err) {
      console.error('Error rejecting ticket:', err);
    }
  };

  const canApprove = () => {
    return ticket?.status === 'PENDING_APPROVAL' && can(Permission.APPROVE_TICKETS);
  };

  const canTakeTicket = () => {
    return (
      can(Permission.ASSIGN_TICKETS) &&
      !ticket?.assignedTo &&
      ticket?.status !== 'PENDING_APPROVAL' &&
      ticket?.status !== 'REJECTED'
    );
  };

  const canEscalateTicket = () => {
    return user?.role === 'AGENT' && ticket?.assignedTo?._id === user?._id;
  };

  const canDeleteTicketBtn = () => can(Permission.DELETE_TICKETS);

  const canChangeStatus = () => {
    return (
      (user?.role === 'AGENT' || user?.role === 'SUPER_ADMIN') &&
      ticket?.status !== 'PENDING_APPROVAL' &&
      ticket?.status !== 'REJECTED'
    );
  };

  const isEndUser = () => {
    return user?.role === 'END_USER';
  };

  const isTicketClosedOrRejected = () => {
    return ticket?.status === 'RESOLVED' || ticket?.status === 'REJECTED' || ticket?.status === 'CLOSED';
  };

  const isTicketPendingApproval = () => {
    return ticket?.status === 'PENDING_APPROVAL';
  };

  const canSendMessages = () => {
    // End User ne može da piše poruke ako je tiket resolved, rejected ili pending approval
    if (isEndUser() && (isTicketClosedOrRejected() || isTicketPendingApproval())) {
      return false;
    }
    return true;
  };

  const handleRequestReopen = async () => {
    try {
      const updatedTicket = await ticketService.requestReopenTicket(id!);
      setTicket(updatedTicket);
      loadActivities();
    } catch (err) {
      console.error('Error requesting reopen:', err);
    }
  };

  const handleCloseTicket = async () => {
    try {
      const updatedTicket = await ticketService.closeTicketAsEndUser(id!);
      setTicket(updatedTicket);
      loadActivities();
    } catch (err) {
      console.error('Error closing ticket:', err);
    }
  };

  const handleAssignToSelf = async () => {
    try {
      const updatedTicket = await ticketService.assignToSelf(id!);
      setTicket(updatedTicket);
      loadActivities();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error taking ticket');
      console.error('Error assigning ticket:', err);
    }
  };

  const handleEscalate = async () => {
    try {
      const updatedTicket = await ticketService.escalateTicket(id!);
      setTicket(updatedTicket);
      loadActivities();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error escalating ticket');
      console.error('Error escalating ticket:', err);
    }
  };

  const handleDeleteTicket = async () => {
    try {
      await ticketService.deleteTicket(id!);
      setDeleteDialogVisible(false);
      navigate('/tickets');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error deleting ticket');
      console.error('Error deleting ticket:', err);
    }
  };

  if (loading) {
    return (
      <div className="spinner-wrapper">
        <div className="spinner">
          <div className="sk-folding-cube">
            <div className="sk-cube1 sk-cube"></div>
            <div className="sk-cube2 sk-cube"></div>
            <div className="sk-cube4 sk-cube"></div>
            <div className="sk-cube3 sk-cube"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="ticket-details-container">
        <Message severity="error" text={error || 'Ticket not found'} />
        <Button 
          label="Back" 
          icon="pi pi-arrow-left" 
          onClick={() => navigate('/tickets')} 
          className="mt-3"
        />
      </div>
    );
  }

  return (
    <div className="ticket-details-container">
      {/* Header */}
      <div className={`ticket-header-modern ${ticket.status === 'PENDING_APPROVAL' ? 'pending-approval' : ''}`}>
        <div className="header-left">
          <Button 
            icon="pi pi-arrow-left" 
            className="p-button-text p-button-rounded back-button" 
            onClick={() => navigate('/tickets')}
            tooltip="Back to tickets"
          />
          <div className="ticket-header-info">
            <div className="ticket-number-badge">{ticket.ticketNumber}</div>
            <h1 className="ticket-title">{ticket.title}</h1>
            {ticket.assignedTo && (
              <div className="assigned-to-badge">
                <i className="pi pi-user"></i>
                <span>Assigned to: {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}</span>
              </div>
            )}
          </div>
        </div>
        <div className="header-right">
          <Badge 
            value={getStatusLabel(ticket.status)} 
            severity={getStatusSeverity(ticket.status)}
            className={`status-badge-large ${ticket.status === 'PENDING_APPROVAL' ? 'p-badge-warning' : ''}`}
          />
          {!ticket.clientCompany && (
            <Badge value="Deleted company" severity="danger" />
          )}
          
          {/* Approval buttons for CLIENT_ADMIN and SUPER_ADMIN when ticket is PENDING_APPROVAL */}
          {canApprove() && (
            <div className="approval-buttons">
              <Button
                label="Approve"
                icon="pi pi-check"
                className="p-button-success approve-btn"
                onClick={handleApproveTicket}
              />
              <Button
                label="Reject"
                icon="pi pi-times"
                className="p-button-danger reject-btn"
                onClick={() => setRejectDialogVisible(true)}
              />
            </div>
          )}
          
          {/* End User controls */}
          {isEndUser() && (
            <div className="approval-buttons">
              {isTicketClosedOrRejected() ? (
                <Button
                  label="Request Reopening"
                  icon="pi pi-refresh"
                  className="p-button-warning"
                  onClick={handleRequestReopen}
                  tooltip="Submit a request to reopen the ticket"
                />
              ) : ticket.status !== 'PENDING_APPROVAL' && (
                <Button
                  label="Close Ticket"
                  icon="pi pi-check-circle"
                  className="p-button-secondary"
                  onClick={handleCloseTicket}
                  tooltip="Close the ticket if the problem is resolved"
                  disabled={ticket.status === 'CLOSED'}
                />
              )}
            </div>
          )}

          {canTakeTicket() && (
            <Button
              label="Take Ticket"
              icon="pi pi-user-plus"
              className="p-button-info"
              onClick={handleAssignToSelf}
              tooltip="Take this ticket and work on it"
            />
          )}

          {canEscalateTicket() && (
            <Button
              label="Escalate"
              icon="pi pi-arrow-up"
              className="p-button-warning"
              onClick={handleEscalate}
              tooltip="Escalate ticket to SUPER_ADMIN"
            />
          )}

          {canChangeStatus() && (
            <Dropdown 
              value={ticket.status}
              options={
                user?.role === 'AGENT'
                  ? statusOptions.filter(
                      (option) => option.value !== 'PENDING_APPROVAL' && option.value !== 'REJECTED'
                    )
                  : statusOptions
              }
              onChange={(e) => handleStatusChange(e.value)}
              placeholder="Change status"
              className="status-dropdown"
            />
          )}

          {canDeleteTicketBtn() && (
            <Button
              icon="pi pi-trash"
              className="p-button-danger p-button-text"
              onClick={() => setDeleteDialogVisible(true)}
              tooltip="Delete ticket"
            />
          )}
        </div>
      </div>

      {ticket.supportUsage && (
        <div className={`ticket-support-banner${ticket.supportUsage.remainingHours <= 0 ? ' is-over' : ''}`}>
          <i className={`pi ${ticket.supportUsage.remainingHours <= 0 ? 'pi-exclamation-triangle' : 'pi-clock'}`} />
          <div>
            {ticket.supportUsage.remainingHours <= 0 ? (
              <>
                <strong>Support package hours are used up</strong>
                <p>
                  {ticket.supportUsage.dealName}: {ticket.supportUsage.usedHours}h / {ticket.supportUsage.includedHours}h
                  {ticket.supportUsage.periodLabel ? ` (${ticket.supportUsage.periodLabel})` : ''}.
                  Additional time on this ticket is billed at{' '}
                  {ticket.supportUsage.overageRate.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}/h.
                </p>
              </>
            ) : (
              <>
                <strong>{ticket.supportUsage.remainingHours}h left in the support package</strong>
                <p>
                  {ticket.supportUsage.dealName}: {ticket.supportUsage.usedHours}h / {ticket.supportUsage.includedHours}h used
                  {ticket.supportUsage.periodLabel ? ` (${ticket.supportUsage.periodLabel})` : ''}.
                  Time over the package is billed at{' '}
                  {ticket.supportUsage.overageRate.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}/h.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="ticket-content-grid">
        {/* Left Column - Details */}
        <div className="ticket-main-column">
          {/* Ticket Info Card */}
          <Card className="details-card modern-card">
            <div className="card-header">
              <i className="pi pi-info-circle"></i>
              <h3>Ticket Details</h3>
            </div>
            <div className="ticket-info-modern">
              <div className="info-row">
                <div className="info-item-modern">
                  <div className="info-icon">
                    <i className="pi pi-tag"></i>
                  </div>
                  <div className="info-content">
                    <span className="info-label-modern">Category</span>
                    <span className="info-value-modern">{ticket.category.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                <div className="info-item-modern">
                  <div className="info-icon">
                    <i className="pi pi-user"></i>
                  </div>
                  <div className="info-content">
                    <span className="info-label-modern">Created by</span>
                    <span className="info-value-modern">
                      {ticket.createdBy
                        ? `${ticket.createdBy.firstName} ${ticket.createdBy.lastName}`
                        : 'Deleted user'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="info-row">
                <div className="info-item-modern">
                  <div className="info-icon">
                    <i className="pi pi-building"></i>
                  </div>
                  <div className="info-content">
                    <span className="info-label-modern">Company</span>
                    <span className="info-value-modern">
                      {ticket.clientCompany?.name || 'Deleted company'}
                    </span>
                  </div>
                </div>
                <div className="info-item-modern">
                  <div className="info-icon">
                    <i className="pi pi-calendar"></i>
                  </div>
                  <div className="info-content">
                    <span className="info-label-modern">Created</span>
                    <span className="info-value-modern">
                      {formatDateTime(ticket.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
              {ticket.assignedTo && (
                <div className="info-row">
                  <div className="info-item-modern">
                    <div className="info-icon">
                      <i className="pi pi-user-edit"></i>
                    </div>
                    <div className="info-content">
                      <span className="info-label-modern">Assigned to</span>
                      <span className="info-value-modern">
                        {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="ticket-description-modern">
              <div className="description-header">
                <i className="pi pi-file-edit"></i>
                <h4>Problem Description</h4>
              </div>
              <p className="description-text">{ticket.description}</p>
            </div>

            {/* Time Display - različito za različite uloge */}
            {(ticket.status === 'CLOSED' || ticket.status === 'RESOLVED') && (
              <>
                {/* END_USER & CLIENT_ADMIN - samo billable vreme (ukupno svih agenata) */}
                {(user?.role === 'END_USER' || user?.role === 'CLIENT_ADMIN') && totalTime && totalTime.billableMinutes > 0 && (
                  <div className="billable-time-box">
                    <div className="time-icon">
                      <i className="pi pi-clock"></i>
                    </div>
                      <div className="time-content">
                        <div className="time-label">Ticket resolution time</div>
                        <div className="time-value">
                          {formatTime(totalTime.billableHours, totalTime.billableMinutesRemainder)}
                        </div>
                        {/* <div className="time-note">Billable vreme</div> */}
                      </div>
                  </div>
                )}

                {/* AGENT - samo svoj actual time */}
                {user?.role === 'AGENT' && agentTime && agentTime.hasWorked && (
                  <div className="actual-time-box">
                    <div className="time-icon">
                      <i className="pi pi-clock"></i>
                    </div>
                    <div className="time-content">
                      <div className="time-label">Your time on ticket</div>
                      <div className="time-value">
                        {formatTime(agentTime.actualHours, agentTime.actualMinutesRemainder)}
                      </div>
                      {/* <div className="time-note">Realno vreme koje ste utrošili</div> */}
                    </div>
                  </div>
                )}

                {/* SUPER_ADMIN - actual + billable vreme (ukupno svih agenata) */}
                {user?.role === 'SUPER_ADMIN' && totalTime && totalTime.billableMinutes > 0 && (
                  <div className="superadmin-time-box">
                    <div className="time-icon">
                      <i className="pi pi-clock"></i>
                    </div>
                    <div className="time-content">
                      <div className="time-label">Total time of all agents</div>
                      <div className="time-value">
                        <div className="actual-time">
                          Actual: {formatTime(totalTime.actualHours, totalTime.actualMinutesRemainder)}
                        </div>
                        <div className="billable-time">
                          Billable: {formatTime(totalTime.billableHours, totalTime.billableMinutesRemainder)}
                        </div>
                      </div>
                      {/* <div className="time-note">Actual = realno vreme | Billable = zaokruženo na 30 min</div> */}
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Chat Section */}
          <Card title="💬 Chat" className="chat-card">
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="empty-chat">
                  <i className="pi pi-comments" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                  <p>No messages. Be the first to write!</p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg._id} 
                    className={`chat-message ${msg.sender._id === user?._id ? 'own-message' : 'other-message'}`}
                  >
                    <div className="message-header">
                      <Avatar 
                        label={msg.sender.firstName.charAt(0)} 
                        shape="circle" 
                        size="normal"
                      />
                      <span className="message-sender">
                        {msg.sender.firstName} {msg.sender.lastName}
                      </span>
                      <span className="message-time">
                        {formatClockTime(msg.createdAt)}
                      </span>
                    </div>
                    <div className="message-content">
                      {msg.message && msg.message !== 'Attachment' && <span>{msg.message}</span>}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="chat-attachments">
                          {msg.attachments.map((attachment) => (
                            <a
                              key={attachment}
                              href={getAssetUrl(attachment)}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img src={getAssetUrl(attachment)} alt="Attachment" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            
            <div className="chat-input">
              {!canSendMessages() ? (
                <div className="chat-disabled-message">
                  <i className="pi pi-lock" style={{ fontSize: '1.5rem', color: '#ef4444', marginBottom: '0.5rem' }}></i>
                  <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0 }}>
                    {ticket.status === 'PENDING_APPROVAL' 
                      ? 'You cannot send messages because the ticket is pending approval.'
                      : ticket.status === 'RESOLVED'
                      ? 'You cannot send messages because the ticket is resolved.'
                      : ticket.status === 'CLOSED'
                      ? 'You cannot send messages because the ticket is closed.'
                      : 'You cannot send messages because the ticket is rejected.'
                    }
                  </p>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {ticket.status === 'PENDING_APPROVAL'
                      ? 'Wait for approval from CLIENT_ADMIN or reject the request.'
                      : 'Request ticket reopening to continue communication.'
                    }
                  </p>
                </div>
              ) : (
                <>
                  {pendingAttachments.length > 0 && (
                    <div className="chat-preview">
                      {pendingAttachments.map((attachment, index) => (
                        <div key={`${attachment.slice(0, 24)}-${index}`} className="chat-preview-item">
                          <img src={attachment} alt="Pending attachment" />
                          <button
                            type="button"
                            onClick={() => setPendingAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachmentError && <div className="chat-attach-error">{attachmentError}</div>}
                  <InputTextarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Write a message or paste a screenshot..."
                    rows={3}
                    autoResize
                    onPaste={(e) => {
                      const items = Array.from(e.clipboardData?.items || []);
                      const imageItem = items.find((item) => item.type.startsWith('image/'));
                      if (!imageItem) {
                        return;
                      }
                      const file = imageItem.getAsFile();
                      if (file) {
                        e.preventDefault();
                        addChatImage(file);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <input
                    ref={chatFileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        addChatImage(file);
                      }
                      e.currentTarget.value = '';
                    }}
                  />
                  <div className="chat-actions">
                    <Button
                      type="button"
                      label="Attach"
                      icon="pi pi-paperclip"
                      severity="secondary"
                      className="chat-attach-btn"
                      onClick={() => chatFileInputRef.current?.click()}
                      disabled={sending}
                    />
                    <Button 
                      label="Send" 
                      icon="pi pi-send" 
                      onClick={handleSendMessage}
                      loading={sending}
                      disabled={!newMessage.trim() && pendingAttachments.length === 0}
                    />
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>

        {/* Right Column - Timeline */}
        <div className="ticket-sidebar">
          <Card className="timeline-card-modern">
            <div className="card-header">
              <i className="pi pi-clock"></i>
              <h3>Timeline</h3>
            </div>
            <div className="timeline-modern">
              {activities.length === 0 ? (
                <div className="empty-timeline">
                  <i className="pi pi-clock" style={{ fontSize: '2rem', color: '#ccc' }}></i>
                  <p>No activities</p>
                </div>
              ) : (
                activities.map((activity: any, index: number) => {
                  const activityTypeMap: any = {
                    CREATED: { icon: 'pi-plus-circle', title: 'Ticket created', className: 'created' },
                    ASSIGNED: { icon: 'pi-user-plus', title: 'Ticket taken', className: 'status-open' },
                    UNASSIGNED: { icon: 'pi-user-minus', title: 'Assignment removed', className: 'status-pending_approval' },
                    ESCALATED: { icon: 'pi-arrow-up', title: 'Ticket escalated', className: 'status-pending_approval' },
                    ESCALATION_APPROVED: { icon: 'pi-check-circle', title: 'Escalation approved', className: 'status-new' },
                    STATUS_CHANGED: { icon: 'pi-refresh', title: 'Status changed', className: `status-${activity.details?.newStatus?.toLowerCase() || 'new'}` },
                    APPROVED: { icon: 'pi-check-circle', title: 'Ticket approved', className: 'status-new' },
                    REJECTED: { icon: 'pi-times-circle', title: 'Ticket rejected', className: 'status-rejected' },
                    CLOSED: { icon: 'pi-lock', title: 'Ticket closed', className: 'status-closed' },
                    REOPENED: { icon: 'pi-refresh', title: 'Reopening request', className: 'status-pending_approval' },
                    MESSAGE_SENT: { icon: 'pi-comment', title: 'Message sent', className: 'chat' }
                  };

                  const config = activityTypeMap[activity.activityType] || { icon: 'pi-info-circle', title: 'Activity', className: 'created' };

                  return (
                    <div key={activity._id || index} className="timeline-item">
                      <div className={`timeline-marker ${config.className}`}>
                        <i className={`pi ${config.icon}`}></i>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-title">{config.title}</div>
                        <div className="timeline-date">
                          {formatDateTime(activity.createdAt)}
                        </div>
                        {activity.performedBy && (
                          <div className="timeline-user">
                            {activity.performedBy.firstName} {activity.performedBy.lastName}
                          </div>
                        )}
                        {activity.details?.note && (
                          <div className="timeline-note">{activity.details.note}</div>
                        )}
                        {activity.details?.message && (
                          <div className="timeline-note">"{activity.details.message}..."</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog
        header="Reject Ticket"
        visible={rejectDialogVisible}
        style={{ width: '450px' }}
        onHide={() => setRejectDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setRejectDialogVisible(false)}
              className="cancel-btn p-button-secondary"
            />
            <Button
              label="Reject Ticket"
              icon="pi pi-check"
              onClick={handleRejectTicket}
              className="p-button-danger"
              disabled={!rejectionReason.trim()}
            />
          </div>
        }
      >
        <div className="p-field">
          <label htmlFor="rejectionReason">Reason for rejection:</label>
          <InputTextarea
            id="rejectionReason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={5}
            placeholder="Enter the reason why you are rejecting this ticket..."
            className="w-full"
            autoFocus
          />
        </div>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Delete Ticket"
        visible={deleteDialogVisible}
        style={{ width: '450px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setDeleteDialogVisible(false)}
              className="cancel-btn p-button-secondary"
            />
            <Button
              label="Delete Ticket"
              icon="pi pi-trash"
              onClick={handleDeleteTicket}
              className="p-button-danger"
              autoFocus
            />
          </div>
        }
      >
        <div className="p-field" style={{ textAlign: 'center', padding: '1rem 0' }}>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#ef4444', marginBottom: '1rem' }}></i>
          <p style={{ fontSize: '1.1rem', color: '#374151', marginBottom: '0.5rem', fontWeight: 600 }}>
            Are you sure you want to delete this ticket?
          </p>
          <p style={{ fontSize: '0.95rem', color: '#6b7280', marginBottom: '0.5rem' }}>
            <strong>Ticket number:</strong> {ticket?.ticketNumber}
          </p>
          <p style={{ fontSize: '0.95rem', color: '#6b7280', marginBottom: '1rem' }}>
            <strong>Title:</strong> {ticket?.title}
          </p>
          <div style={{ 
            background: '#fee2e2', 
            border: '2px solid #fca5a5', 
            borderRadius: '8px', 
            padding: '0.75rem', 
            color: '#991b1b',
            fontSize: '0.875rem'
          }}>
            <i className="pi pi-info-circle" style={{ marginRight: '0.5rem' }}></i>
            <strong>Warning:</strong> This action cannot be undone. All ticket data, messages and history will be permanently deleted.
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TicketDetails;

